'use client'

import { useState, useEffect } from 'react'
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  XCircleIcon,
  XMarkIcon 
} from '@heroicons/react/20/solid'

const Toast = ({ type = 'success', message, isVisible, onDismiss, autoHide = true, duration = 5000 }) => {
  const [show, setShow] = useState(isVisible)

  useEffect(() => {
    setShow(isVisible)
  }, [isVisible])

  useEffect(() => {
    if (show && autoHide) {
      const timer = setTimeout(() => {
        handleDismiss()
      }, duration)
      return () => clearTimeout(timer)
    }
  }, [show, autoHide, duration])

  const handleDismiss = () => {
    setShow(false)
    setTimeout(() => {
      if (onDismiss) onDismiss()
    }, 300) // Wait for animation to complete
  }

  const getToastConfig = () => {
    switch (type) {
      case 'success':
        return {
          bgClass: 'bg-green-50 dark:bg-green-500/30 dark:outline dark:outline-green-500/40',
          iconClass: 'text-green-400',
          textClass: 'text-green-800 dark:text-green-200',
          buttonClass: 'bg-green-50 text-green-500 hover:bg-green-100 focus-visible:ring-green-600 focus-visible:ring-offset-green-50 dark:bg-transparent dark:text-green-300 dark:hover:bg-green-500/20 dark:focus-visible:ring-green-500 dark:focus-visible:ring-offset-green-900',
          icon: CheckCircleIcon
        }
      case 'warning':
        return {
          bgClass: 'bg-yellow-50 dark:bg-yellow-500/30 dark:outline dark:outline-yellow-500/40',
          iconClass: 'text-yellow-400',
          textClass: 'text-yellow-800 dark:text-yellow-200',
          buttonClass: 'bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus-visible:ring-yellow-600 focus-visible:ring-offset-yellow-50 dark:bg-transparent dark:text-yellow-300 dark:hover:bg-yellow-500/20 dark:focus-visible:ring-yellow-500 dark:focus-visible:ring-offset-yellow-900',
          icon: ExclamationTriangleIcon
        }
      case 'error':
        return {
          bgClass: 'bg-red-50 dark:bg-red-500/30 dark:outline dark:outline-red-500/40',
          iconClass: 'text-red-400',
          textClass: 'text-red-800 dark:text-red-200',
          buttonClass: 'bg-red-50 text-red-500 hover:bg-red-100 focus-visible:ring-red-600 focus-visible:ring-offset-red-50 dark:bg-transparent dark:text-red-300 dark:hover:bg-red-500/20 dark:focus-visible:ring-red-500 dark:focus-visible:ring-offset-red-900',
          icon: XCircleIcon
        }
      default:
        return getToastConfig('success')
    }
  }

  const config = getToastConfig()
  const Icon = config.icon

  if (!show) return null

  return (
    <div className={`fixed top-4 right-4 z-[9999] transition-all duration-300 ease-in-out transform ${
      show ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
    }`}>
      <div className={`rounded-md p-4 shadow-lg max-w-sm ${config.bgClass}`}>
        <div className="flex">
          <div className="shrink-0">
            <Icon aria-hidden="true" className={`size-5 ${config.iconClass}`} />
          </div>
          <div className="ml-3">
            <p className={`text-sm font-medium ${config.textClass}`}>
              {message}
            </p>
          </div>
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={handleDismiss}
                className={`inline-flex rounded-md p-1.5 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden ${config.buttonClass}`}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Toast

